import { Navigate, type RouteObject } from 'react-router-dom';

import { PERMISSIONS } from '@solar/contracts';

import { RequireAnonymous, RequireAuth } from '../features/auth/RequireAuth';
import { AppLayout } from '../layouts/AppLayout';
import { RootLayout } from '../layouts/RootLayout';
import { AdminApplicationDetailPage } from '../pages/app/admin/AdminApplicationDetailPage';
import { AdminApplicationsPage } from '../pages/app/admin/AdminApplicationsPage';
import { AdminAuditLogDetailPage, AdminAuditLogsPage } from '../pages/app/admin/AdminAuditLogPage';
import { AdminContractsPage } from '../pages/app/admin/AdminContractsPage';
import { AdminCustomerDetailPage } from '../pages/app/admin/AdminCustomerDetailPage';
import { AdminCustomersPage } from '../pages/app/admin/AdminCustomersPage';
import { AdminDiscountsReviewPage } from '../pages/app/admin/AdminDiscountsReviewPage';
import { AdminMandateCancellationsPage } from '../pages/app/admin/AdminMandateCancellationsPage';
import { AdminMandateDetailPage } from '../pages/app/admin/AdminMandateDetailPage';
import { AdminMandatesPage } from '../pages/app/admin/AdminMandatesPage';
import { AdminProductFormPage } from '../pages/app/admin/AdminProductFormPage';
import { AdminProductsPage } from '../pages/app/admin/AdminProductsPage';
import { AdminProviderBanksPage } from '../pages/app/admin/AdminProviderBanksPage';
import { AdminRepaymentPlanFormPage } from '../pages/app/admin/AdminRepaymentPlanFormPage';
import { AdminRepaymentPlansPage } from '../pages/app/admin/AdminRepaymentPlansPage';
import { AdminRoleDetailPage } from '../pages/app/admin/AdminRoleDetailPage';
import { AdminRolesPage } from '../pages/app/admin/AdminRolesPage';
import { AdminUserCreatePage } from '../pages/app/admin/AdminUserCreatePage';
import { AdminUserDetailPage } from '../pages/app/admin/AdminUserDetailPage';
import { AdminUsersPage } from '../pages/app/admin/AdminUsersPage';
import { AdminPaymentReceiptPage } from '../pages/app/admin/billing/AdminPaymentReceiptPage';
import { AdminPaymentsPage } from '../pages/app/admin/billing/AdminPaymentsPage';
import { BillingDashboardPage } from '../pages/app/admin/billing/BillingDashboardPage';
import { PaymentAttemptsPage } from '../pages/app/admin/billing/PaymentAttemptsPage';
import { RepaymentDetailPage } from '../pages/app/admin/billing/RepaymentDetailPage';
import { NotificationOutboxPage } from '../pages/app/admin/notifications/NotificationOutboxPage';
import { ReportsPage } from '../pages/app/admin/reports/ReportsPage';
import { SystemHealthPage } from '../pages/app/admin/SystemHealthPage';
import { AddressesPage } from '../pages/app/customer/AddressesPage';
import { ApplicationDetailPage } from '../pages/app/customer/ApplicationDetailPage';
import { ApplicationsListPage } from '../pages/app/customer/ApplicationsListPage';
import { ApplicationWizardPage } from '../pages/app/customer/ApplicationWizardPage';
import { BankAccountsPage } from '../pages/app/customer/BankAccountsPage';
import { ConsentsPage } from '../pages/app/customer/ConsentsPage';
import { ContractDetailPage } from '../pages/app/customer/ContractDetailPage';
import { ContractsPage } from '../pages/app/customer/ContractsPage';
import { CustomerTimelinePage } from '../pages/app/customer/CustomerTimelinePage';
import { MandateDetailPage } from '../pages/app/customer/MandateDetailPage';
import { MandateSetupPage } from '../pages/app/customer/MandateSetupPage';
import { OnboardingPage } from '../pages/app/customer/OnboardingPage';
import { PaymentAuthorizePage } from '../pages/app/customer/payments/PaymentAuthorizePage';
import { PaymentHistoryPage } from '../pages/app/customer/payments/PaymentHistoryPage';
import { PaymentReceiptPage } from '../pages/app/customer/payments/PaymentReceiptPage';
import { PlansPage } from '../pages/app/customer/PlansPage';
import { ProductDetailPage } from '../pages/app/customer/ProductDetailPage';
import { ProductsPage } from '../pages/app/customer/ProductsPage';
import { CustomerProfilePage } from '../pages/app/customer/ProfilePage';
import { RepaymentsPage } from '../pages/app/customer/repayments/RepaymentsPage';
import { DashboardPage } from '../pages/app/DashboardPage';
import { ProfilePage } from '../pages/app/ProfilePage';
import { SessionsPage } from '../pages/app/SessionsPage';
import { UnauthorizedPage } from '../pages/app/UnauthorizedPage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { ResendVerificationPage } from '../pages/auth/RequestLinkPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '../pages/auth/VerifyEmailPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { RouteErrorPage } from '../pages/RouteErrorPage';
import { StatusPage } from '../pages/StatusPage';

import { ROUTES } from './paths';

/**
 * Route table.
 *
 * Declared as data rather than JSX so tests can mount the same tree through
 * `createMemoryRouter` without duplicating the definition.
 */
export const routes: RouteObject[] = [
  {
    path: ROUTES.home,
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to={ROUTES.login} replace /> },
      { path: ROUTES.status.slice(1), element: <StatusPage /> },
    ],
  },

  {
    element: <RequireAnonymous />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: ROUTES.login, element: <LoginPage /> },
      { path: ROUTES.register, element: <RegisterPage /> },
      { path: ROUTES.forgotPassword, element: <ForgotPasswordPage /> },
      { path: ROUTES.resendVerification, element: <ResendVerificationPage /> },
    ],
  },

  {
    errorElement: <RouteErrorPage />,
    children: [
      { path: ROUTES.verifyEmail, element: <VerifyEmailPage /> },
      { path: ROUTES.resetPassword, element: <ResetPasswordPage /> },
    ],
  },

  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: ROUTES.dashboard,
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'sessions', element: <SessionsPage /> },
          { path: 'unauthorized', element: <UnauthorizedPage /> },

          {
            element: <RequireAuth permission={PERMISSIONS.CUSTOMERS_READ} />,
            children: [
              { path: 'customer/onboarding', element: <OnboardingPage /> },
              { path: 'customer/profile', element: <CustomerProfilePage /> },
              { path: 'customer/addresses', element: <AddressesPage /> },
              { path: 'customer/consents', element: <ConsentsPage /> },
              { path: 'customer/timeline', element: <CustomerTimelinePage /> },
              { path: 'customer/products', element: <ProductsPage /> },
              { path: 'customer/products/:id', element: <ProductDetailPage /> },
              { path: 'customer/plans', element: <PlansPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.SUBSCRIPTIONS_READ} />,
            children: [
              { path: 'customer/apply', element: <ApplicationWizardPage /> },
              { path: 'customer/applications', element: <ApplicationsListPage /> },
              { path: 'customer/applications/:id', element: <ApplicationDetailPage /> },
              { path: 'customer/contracts', element: <ContractsPage /> },
              { path: 'customer/contracts/:id', element: <ContractDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.MANDATES_READ} />,
            children: [{ path: 'customer/mandates/:id', element: <MandateDetailPage /> }],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.MANDATES_CREATE} />,
            children: [
              {
                path: 'customer/contracts/:contractId/mandate/setup',
                element: <MandateSetupPage />,
              },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.BANK_ACCOUNTS_READ_MASKED} />,
            children: [{ path: 'customer/bank-accounts', element: <BankAccountsPage /> }],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.BILLING_READ} />,
            children: [
              { path: 'customer/repayments', element: <RepaymentsPage /> },
              { path: 'customer/payments', element: <PaymentHistoryPage /> },
              { path: 'customer/payments/:id/receipt', element: <PaymentReceiptPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.PAYMENTS_AUTHORIZE_OWN} />,
            children: [
              {
                path: 'customer/payments/authorize/:authorizationId',
                element: <PaymentAuthorizePage />,
              },
              { path: 'customer/payments/authorize', element: <PaymentAuthorizePage /> },
            ],
          },

          {
            element: <RequireAuth permission={PERMISSIONS.REPORTS_READ} />,
            children: [{ path: 'admin/reports', element: <ReportsPage /> }],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.NOTIFICATIONS_READ} />,
            children: [{ path: 'admin/notifications/outbox', element: <NotificationOutboxPage /> }],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.SYSTEM_HEALTH_READ} />,
            children: [{ path: 'admin/system-health', element: <SystemHealthPage /> }],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.USERS_READ} />,
            children: [
              { path: 'admin/users', element: <AdminUsersPage /> },
              { path: 'admin/users/new', element: <AdminUserCreatePage /> },
              { path: 'admin/users/:id', element: <AdminUserDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.ROLES_MANAGE} />,
            children: [
              { path: 'admin/roles', element: <AdminRolesPage /> },
              { path: 'admin/roles/:id', element: <AdminRoleDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.AUDIT_READ} />,
            children: [
              { path: 'admin/audit-logs', element: <AdminAuditLogsPage /> },
              { path: 'admin/audit-logs/:id', element: <AdminAuditLogDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.CUSTOMERS_READ} />,
            children: [
              { path: 'admin/customers', element: <AdminCustomersPage /> },
              { path: 'admin/customers/:id', element: <AdminCustomerDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.PLANS_MANAGE} />,
            children: [
              { path: 'admin/products', element: <AdminProductsPage /> },
              { path: 'admin/products/new', element: <AdminProductFormPage /> },
              { path: 'admin/products/:id', element: <AdminProductFormPage /> },
              { path: 'admin/repayment-plans', element: <AdminRepaymentPlansPage /> },
              { path: 'admin/repayment-plans/new', element: <AdminRepaymentPlanFormPage /> },
              { path: 'admin/repayment-plans/:id', element: <AdminRepaymentPlanFormPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.MANDATES_READ} />,
            children: [
              { path: 'admin/mandates', element: <AdminMandatesPage /> },
              { path: 'admin/mandates/:id', element: <AdminMandateDetailPage /> },
              {
                path: 'admin/mandate-cancellations',
                element: <AdminMandateCancellationsPage />,
              },
              {
                path: 'admin/mandate-cancellations/:id',
                element: <AdminMandateCancellationsPage />,
              },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.BILLING_READ} />,
            children: [
              { path: 'admin/billing', element: <BillingDashboardPage /> },
              { path: 'admin/billing/repayments/:id', element: <RepaymentDetailPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.PAYMENT_ATTEMPTS_READ} />,
            children: [
              { path: 'admin/payment-attempts', element: <PaymentAttemptsPage /> },
              { path: 'admin/payment-attempts/:id', element: <PaymentAttemptsPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.PAYMENTS_READ} />,
            children: [
              { path: 'admin/payments', element: <AdminPaymentsPage /> },
              { path: 'admin/payments/:id', element: <AdminPaymentsPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.RECEIPTS_READ} />,
            children: [
              { path: 'admin/payments/:id/receipt', element: <AdminPaymentReceiptPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.PROVIDER_BANKS_READ} />,
            children: [
              { path: 'admin/payment-provider/banks', element: <AdminProviderBanksPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.SUBSCRIPTIONS_READ} />,
            children: [
              { path: 'admin/applications', element: <AdminApplicationsPage /> },
              { path: 'admin/applications/:id', element: <AdminApplicationDetailPage /> },
              { path: 'admin/contracts', element: <AdminContractsPage /> },
            ],
          },
          {
            element: <RequireAuth permission={PERMISSIONS.SUBSCRIPTIONS_UPDATE} />,
            children: [{ path: 'admin/discounts-review', element: <AdminDiscountsReviewPage /> }],
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage />, errorElement: <RouteErrorPage /> },
];
